// All user-facing copy lives here. Swedish is the default; English is the
// fallback for any key missing in the active language (and a full mirror for
// caregivers abroad). Code and comments stay in English per the contract.

export type Lang = "sv" | "en";

const sv = {
  "app.name": "Phoenix",

  "tabs.today": "Idag",
  "tabs.talk": "Prata",
  "tabs.settings": "Inställningar",

  "today.greeting": "God dag",
  "today.greeting.morning": "God morgon",
  "today.greeting.day": "God dag",
  "today.greeting.evening": "God kväll",
  "today.greeting.night": "God kväll",
  "today.loading": "Hämtar dagen …",
  "today.offline.saved": "Sparat på din enhet — skickas när nätet är tillbaka.",
  "today.checkin.mood.title": "Hur mår du just nu?",
  "today.checkin.mood.1": "Mycket tungt",
  "today.checkin.mood.2": "Tungt",
  "today.checkin.mood.3": "Helt okej",
  "today.checkin.mood.4": "Bra",
  "today.checkin.mood.5": "Mycket bra",
  "today.checkin.plan.title": "Hur stor dag orkar du?",
  "today.checkin.plan.rest": "Vilodag",
  "today.checkin.plan.rest.hint": "Ingen plan i dag — det räcker att vara.",
  "today.checkin.plan.small": "Liten",
  "today.checkin.plan.small.hint": "En sak räcker gott.",
  "today.checkin.plan.medium": "Mellan",
  "today.checkin.plan.medium.hint": "Några saker i lugn takt.",
  "today.checkin.plan.large": "Stor",
  "today.checkin.plan.large.hint": "En dag med fart.",
  "today.checkin.back": "Tillbaka",
  "today.checkin.done": "Tack. Dagen är igång.",
  "today.checkin.rejected": "Det gick inte att skicka in just nu. Försök igen om en stund.",
  "today.tasks.title": "Dagens saker",
  "today.tasks.empty": "Inga uppgifter i dag. Vila gott.",
  "today.tasks.rest": "Vilodag — inga måsten.",
  "today.streak": "dagar i rad",
  "today.streak.paused": "Sviten vilar — den väntar på dig.",
  "today.points": "poäng",
  "today.error": "Kunde inte hämta dagen. Försök igen om en stund.",
  "today.retry": "Försök igen",

  "talk.title": "Prata",
  "talk.placeholder": "Skriv här …",
  "talk.send": "Skicka",
  "talk.hold": "Håll för att prata",
  "talk.listening": "Jag lyssnar …",
  "talk.micDenied": "Mikrofonen är inte tillåten. Du kan skriva i stället.",
  "talk.error": "Något gick fel. Försök igen.",
  "talk.photo": "Foto",
  "talk.photoRemove": "Ta bort bilden",
  "talk.photoError": "Bilden kunde inte läsas. Försök igen.",

  "brief.empty": "Inget veckobrev ännu. Det första kommer på söndag.",

  "settings.title": "Inställningar",
  "settings.push.title": "Påminnelser",
  "settings.push.hint": "Få en stilla påminnelse på morgonen och kvällen.",
  "settings.push.enable": "Slå på påminnelser",
  "settings.push.enabled": "Påminnelser är på.",
  "settings.push.denied": "Webbläsaren blockerar notiser. Ändra i webbläsarens inställningar.",
  "settings.push.unsupported": "Den här webbläsaren stödjer inte påminnelser.",
  "settings.push.error": "Kunde inte slå på påminnelser. Försök igen.",
  "settings.ios.title": "Lägg till på hemskärmen först",
  "settings.ios.body":
    "På iPhone behöver Phoenix ligga på hemskärmen för att kunna skicka påminnelser. " +
    "Tryck på Dela-knappen (fyrkanten med pilen) nere i Safari och välj " +
    "”Lägg till på hemskärmen”. Öppna sedan Phoenix därifrån.",

  "common.close": "Stäng",
  "common.offline": "Ingen nätkontakt just nu.",

  // ===== shell (app shell: router, error boundary, account, text size) =====
  "shell.tabs.journey": "Min resa",
  "shell.error.title": "Något gick fel här",
  "shell.error.body": "Den här sidan kunde inte visas just nu. Resten av appen fungerar.",
  "shell.error.retry": "Försök igen",
  "settings.account.title": "Konto",
  "settings.account.signedInAs": "Inloggad som",
  "settings.account.signedOut": "Du är inte inloggad.",
  "settings.account.logout": "Logga ut",
  "settings.textsize.title": "Textstorlek",
  "settings.textsize.hint": "Välj den storlek som är lättast att läsa.",
  "settings.textsize.normal": "Normal",
  "settings.textsize.large": "Stor",
  "settings.textsize.largest": "Störst",
  // ===== end shell =====

  // ===== onboarding (appended block — keep at the end of the sv table) =====
  "onboard.step": "Steg",
  "onboard.of": "av",
  "onboard.skip": "Hoppa över",
  "onboard.next": "Fortsätt",
  "onboard.failed": "Det gick inte just nu. Vi går vidare — du kan fylla i det senare.",

  "onboard.welcome.title": "Phoenix.",
  "onboard.welcome.tagline": "Din dagliga följeslagare.",

  "onboard.login.email": "E-post",
  "onboard.login.password": "Lösenord",
  "onboard.login.submit": "Logga in",
  "onboard.login.working": "Loggar in …",
  "onboard.login.error": "Fel e-post eller lösenord.",
  "onboard.login.offline": "Ingen kontakt med servern just nu. Försök igen om en stund.",

  "onboard.infusion.title": "Senaste droppet",
  "onboard.infusion.q": "När fick du din senaste behandling (droppet)?",
  "onboard.infusion.hint": "Då vet jag var i kuren du är.",
  "onboard.infusion.save": "Spara",
  "onboard.infusion.unknown": "Vet inte just nu",

  "onboard.meds.title": "Fota medicinerna",
  "onboard.meds.hint": "Ta en bild på förpackningarna eller på listan. En bild i taget går bra.",
  "onboard.meds.take": "Ta en bild",
  "onboard.meds.again": "Ta en bild till",
  "onboard.meds.done": "Klar",
  "onboard.meds.reading": "Läser bilden …",
  "onboard.meds.review": "Sparat för granskning — inget ändras förrän en människa har bekräftat det.",
  "onboard.meds.failed": "Bilden gick inte att läsa. Du kan ta en till.",


  "onboard.body.title": "Längd och vikt",
  "onboard.body.hint": "Bra att ha som utgångspunkt. Ungefär räcker.",
  "onboard.body.height": "Längd (cm)",
  "onboard.body.weight": "Vikt (kg)",
  "onboard.body.save": "Spara",

  "onboard.push.title": "Påminnelser",
  "onboard.push.q": "Får jag höra av mig på morgonen?",
  "onboard.push.hint": "En stilla påminnelse. Aldrig mitt i natten.",
  "onboard.push.yes": "Ja, gärna",
  "onboard.push.later": "Kanske senare",
  "onboard.push.on": "Påminnelser är på.",
  "onboard.push.denied": "Webbläsaren blockerar notiser. Du kan slå på dem i Inställningar senare.",
  "onboard.push.unsupported": "Den här webbläsaren kan inte skicka påminnelser.",
  "onboard.push.error": "Det gick inte just nu. Du kan slå på dem i Inställningar senare.",
  "onboard.push.ios.title": "Lägg till Phoenix på hemskärmen först",
  "onboard.push.ios.body":
    "På iPhone behöver Phoenix ligga på hemskärmen för att kunna påminna dig. " +
    "Tryck på Dela-knappen (fyrkanten med pilen) nere i Safari och välj " +
    "”Lägg till på hemskärmen”. Öppna sedan Phoenix därifrån.",
  "onboard.push.ios.added": "Jag har lagt till den",

  "onboard.done.title": "Klart.",
  "onboard.done.nothing": "Vi tog det lugnt den här gången. Allt går att fylla i senare.",
  "onboard.done.day": "Du är på dag",
  "onboard.done.sinceInfusion": "sedan behandlingen.",
  "onboard.done.photos.one": "1 bild är sparad för granskning.",
  "onboard.done.photos.many": "bilder är sparade för granskning.",
  "onboard.done.body": "Längd och vikt är sparade.",
  "onboard.done.open": "Öppna Idag",

  // ===========================================================================
  // APPENDED BLOCK — Idag + Min resa (data views). Keys: idag2.* and resa.*
  // Append new keys for these two views at the END of this block only.
  // ===========================================================================
  "idag2.greeting.morning": "God morgon",
  "idag2.greeting.day": "God dag",
  "idag2.greeting.evening": "God kväll",
  "idag2.greeting.night": "God natt",
  "idag2.needLogin": "Logga in först, så hämtar jag din dag.",
  "idag2.cycle.day": "Dag",
  "idag2.cycle.of": "av",
  "tabs.mat": "Mat",
  "mat.title": "Maten",
  "mat.hint": "Dagens meny, veckan framåt och handlingslistan. Ett erbjudande, ingen kvot — en halv portion som åks upp slår en hel som står kvar.",
  "mat.loading": "Hämtar menyn …",
  "mat.failed": "Menyn kunde inte hämtas just nu.",
  "mat.retry": "Försök igen",
  "mat.slot.frukost": "Frukost",
  "mat.slot.mellanmal": "Mellanmål",
  "mat.slot.lunch": "Lunch",
  "mat.slot.middag": "Middag",
  "mat.today.title": "Dagens meny",
  "mat.today.soft": "Öm mun-läge: allt på dagens meny är mjukt, svalt och utan citrus.",
  "mat.today.totals": "Hela menyn: ca %KCAL% kcal och %PROT% g protein.",
  "mat.today.target": "Ditt proteinmål är %LO%–%HI% g per dag.",
  "mat.today.noquota": "Menyn är tillåtande räknad — allt behöver inte ätas.",
  "mat.detail.ingredients": "Ingredienser",
  "mat.detail.steps": "Så här gör du",
  "mat.week.title": "Veckan framåt",
  "mat.week.soft": "mjuk dag",
  "mat.shop.title": "Handlingslista",
  "mat.shop.hint": "Allt för de kommande sju dagarnas meny. Bocka av i affären — listan minns till veckan är slut.",
  "mat.shop.reset": "Nollställ listan",
  "idag2.band.title": "De närmaste två veckorna",
  "idag2.band.hint": "Tryck på en dag för att se vad som brukar hända då.",
  "idag2.band.note": "Förväntat mönster för din kur — dina dagar kan skilja sig.",
  "idag2.band.adjusted": "Prognosen är justerad efter dina egna incheckningar — varje svar gör den träffsäkrare.",
  "idag2.band.tunedWin": "Dina dagar pekar mot dag %FROM%–%TO% (förväntat %BFROM%–%BTO%).",
  "idag2.band.infection": "Infektionskänsligt",
  "idag2.band.calm": "Inget särskilt väntat den här dagen.",
  "idag2.band.noNext": "Nästa behandling är inte inlagd än.",
  "idag2.anchor.title": "Ingen behandlingsdag inlagd än.",
  "idag2.anchor.body": "Skriv in dagen för din senaste behandling, så vet jag var i cykeln du är.",
  "idag2.anchor.label": "Datum för senaste behandlingen",
  "idag2.anchor.save": "Spara dagen",
  "idag2.anchor.saved": "Tack. Nu vet jag var i cykeln du är.",
  "idag2.anchor.error": "Det gick inte att spara just nu. Försök igen om en stund.",
  "idag2.anchor.eventTitle": "Behandling",
  "idag2.weight.title": "Vill du väga dig?",
  "idag2.weight.hint": "Skriv vikten i kilo, eller hoppa över.",
  "idag2.weight.unit": "kg",
  "idag2.sleep.title": "Hur många timmar sov du?",
  "idag2.sleep.hint": "Ungefär räcker gott, eller hoppa över.",
  "idag2.sleep.unit": "timmar",
  "idag2.appetite.title": "Hur är aptiten?",
  "idag2.appetite.none": "Ingen",
  "idag2.appetite.little": "Liten",
  "idag2.appetite.ok": "Helt ok",
  "idag2.appetite.good": "God",
  "idag2.checkin.save": "Spara",
  "idag2.checkin.skip": "Hoppa över",
  "idag2.checkin.saved": "Tack — det är sparat.",
  "idag2.num.invalid": "Skriv ett tal med siffror, eller hoppa över.",
  "idag2.plan.title": "Dagens plan",
  "idag2.plan.empty": "Inget planerat i dag. Vila är också en uppgift.",
  "idag2.plan.markDone": "Klar",
  "idag2.plan.isDone": "Klart",
  "idag2.streak.one": "1 dag i rad",
  "idag2.streak.many": "dagar i rad",
  "idag2.detail.why": "Varför",
  "idag2.detail.how": "Så här gör du",
  "idag2.detail.john": "Gör den tillsammans med John i Prata",
  "idag2.brief.here": "Ditt veckobrev är här",
  "idag2.brief.open": "Läs veckobrevet",

  "resa.tab": "Min resa",
  "resa.title": "Min resa",
  "resa.loading": "Hämtar din resa …",
  "resa.error": "Kunde inte hämta just nu. Försök igen om en stund.",
  "resa.retry": "Försök igen",
  "resa.needLogin": "Logga in först, så visar jag din resa.",
  "resa.weight.title": "Vikt",
  "resa.weight.period": "De senaste två månaderna.",
  "resa.weight.latest": "Senaste vägningen:",
  "resa.weight.unit": "kg",
  "resa.weight.empty": "För få mätningar än — väg dig några morgnar så ritas linjen.",
  "resa.gki.title": "Dina mätvärden (GKI)",
  "resa.gki.hint": "Från sticken: fastande på morgonen och före middagen.",
  "resa.gki.latest": "Senaste värdet:",
  "resa.gki.zone.deep": "Djup terapeutisk ketos",
  "resa.gki.zone.high": "Hög terapeutisk ketos",
  "resa.gki.zone.moderate": "Måttlig ketos",
  "resa.gki.zone.low": "Låg ketos",
  "resa.gki.zone.none": "Ingen ketos",
  "resa.mood.title": "Humör",
  "resa.mood.hint": "De senaste 30 dagarna. Ju högre prick, desto bättre dag.",
  "resa.mood.latest": "Senast:",
  "resa.mood.empty": "Inga svar än den här månaden.",
  "resa.brief.title": "Veckobrevet",
  "resa.brief.weekOf": "Veckan som började",
  "resa.brief.more": "Läs hela",
  "resa.brief.less": "Visa mindre",

  // ===== prata (appended section — keep at the end of the sv table) =====
  "prata2.starter": "Hej Björn. Fråga mig vad som helst — eller ta en bild på maten.",
  "prata2.photo.group": "Ta en bild",
  "prata2.btn.food": "Kan jag äta det här?",
  "prata2.btn.meds": "Fota medicinen",
  "prata2.btn.reading": "Fota mätaren",
  "prata2.ask.food": "Kan jag äta det här?",
  "prata2.ask.meds": "Här är min medicin.",
  "prata2.ask.reading": "Här är mitt mätvärde.",
  "prata2.voice.wait": "Ett ögonblick …",
  "prata2.starter.john": "Jag är John. Vi tar det lugnt — skriv när du vill börja, så andas vi tillsammans.",
} as const;

export type StringKey = keyof typeof sv;

const en: Record<StringKey, string> = {
  "app.name": "Phoenix",

  "tabs.today": "Today",
  "tabs.talk": "Talk",
  "tabs.settings": "Settings",

  "today.greeting": "Good day",
  "today.greeting.morning": "Good morning",
  "today.greeting.day": "Good day",
  "today.greeting.evening": "Good evening",
  "today.greeting.night": "Good evening",
  "today.loading": "Loading your day …",
  "today.offline.saved": "Saved on your device — will be sent when you are back online.",
  "today.checkin.mood.title": "How are you feeling right now?",
  "today.checkin.mood.1": "Very heavy",
  "today.checkin.mood.2": "Heavy",
  "today.checkin.mood.3": "Okay",
  "today.checkin.mood.4": "Good",
  "today.checkin.mood.5": "Very good",
  "today.checkin.plan.title": "How big a day do you have energy for?",
  "today.checkin.plan.rest": "Rest day",
  "today.checkin.plan.rest.hint": "No plan today — just being is enough.",
  "today.checkin.plan.small": "Small",
  "today.checkin.plan.small.hint": "One thing is plenty.",
  "today.checkin.plan.medium": "Medium",
  "today.checkin.plan.medium.hint": "A few things at a calm pace.",
  "today.checkin.plan.large": "Large",
  "today.checkin.plan.large.hint": "A day with some speed.",
  "today.checkin.back": "Back",
  "today.checkin.done": "Thank you. The day has started.",
  "today.checkin.rejected": "It could not be submitted right now. Please try again in a moment.",
  "today.tasks.title": "Today's things",
  "today.tasks.empty": "No tasks today. Rest well.",
  "today.tasks.rest": "Rest day — nothing you must do.",
  "today.streak": "days in a row",
  "today.streak.paused": "The streak is resting — it is waiting for you.",
  "today.points": "points",
  "today.error": "Could not load the day. Try again in a moment.",
  "today.retry": "Try again",

  "talk.title": "Talk",
  "talk.placeholder": "Type here …",
  "talk.send": "Send",
  "talk.hold": "Hold to talk",
  "talk.listening": "I am listening …",
  "talk.micDenied": "Microphone access is blocked. You can type instead.",
  "talk.error": "Something went wrong. Please try again.",
  "talk.photo": "Photo",
  "talk.photoRemove": "Remove the picture",
  "talk.photoError": "The picture could not be read. Please try again.",

  "brief.empty": "No weekly letter yet. The first one arrives on Sunday.",

  "settings.title": "Settings",
  "settings.push.title": "Reminders",
  "settings.push.hint": "Get a gentle reminder in the morning and evening.",
  "settings.push.enable": "Turn on reminders",
  "settings.push.enabled": "Reminders are on.",
  "settings.push.denied": "The browser is blocking notifications. Change it in the browser settings.",
  "settings.push.unsupported": "This browser does not support reminders.",
  "settings.push.error": "Could not turn on reminders. Please try again.",
  "settings.ios.title": "Add to your home screen first",
  "settings.ios.body":
    "On iPhone, Phoenix needs to live on the home screen to send reminders. " +
    "Tap the Share button (the square with the arrow) at the bottom of Safari and choose " +
    "“Add to Home Screen”. Then open Phoenix from there.",

  "common.close": "Close",
  "common.offline": "No network connection right now.",

  // ===== shell (app shell: router, error boundary, account, text size) =====
  "shell.tabs.journey": "My journey",
  "shell.error.title": "Something went wrong here",
  "shell.error.body": "This page could not be shown right now. The rest of the app works.",
  "shell.error.retry": "Try again",
  "settings.account.title": "Account",
  "settings.account.signedInAs": "Signed in as",
  "settings.account.signedOut": "You are not signed in.",
  "settings.account.logout": "Log out",
  "settings.textsize.title": "Text size",
  "settings.textsize.hint": "Pick the size that is easiest to read.",
  "settings.textsize.normal": "Normal",
  "settings.textsize.large": "Large",
  "settings.textsize.largest": "Largest",
  // ===== end shell =====

  // ===== onboarding (appended block — keep at the end of the en table) =====
  "onboard.step": "Step",
  "onboard.of": "of",
  "onboard.skip": "Skip",
  "onboard.next": "Continue",
  "onboard.failed": "That did not work just now. We move on — you can fill it in later.",

  "onboard.welcome.title": "Phoenix.",
  "onboard.welcome.tagline": "Your daily companion.",

  "onboard.login.email": "Email",
  "onboard.login.password": "Password",
  "onboard.login.submit": "Log in",
  "onboard.login.working": "Logging in …",
  "onboard.login.error": "Wrong email or password.",
  "onboard.login.offline": "No contact with the server right now. Try again in a moment.",

  "onboard.infusion.title": "The last infusion",
  "onboard.infusion.q": "When did you get your last treatment (the drip)?",
  "onboard.infusion.hint": "Then I know where in the cycle you are.",
  "onboard.infusion.save": "Save",
  "onboard.infusion.unknown": "I do not know right now",

  "onboard.meds.title": "Photograph the medicines",
  "onboard.meds.hint": "Take a picture of the boxes or of the list. One picture at a time is fine.",
  "onboard.meds.take": "Take a picture",
  "onboard.meds.again": "Take another picture",
  "onboard.meds.done": "Done",
  "onboard.meds.reading": "Reading the picture …",
  "onboard.meds.review": "Saved for review — nothing changes until a person has confirmed it.",
  "onboard.meds.failed": "The picture could not be read. You can take another one.",


  "onboard.body.title": "Height and weight",
  "onboard.body.hint": "Good to have as a starting point. Roughly is enough.",
  "onboard.body.height": "Height (cm)",
  "onboard.body.weight": "Weight (kg)",
  "onboard.body.save": "Save",

  "onboard.push.title": "Reminders",
  "onboard.push.q": "May I get in touch in the morning?",
  "onboard.push.hint": "A quiet reminder. Never in the middle of the night.",
  "onboard.push.yes": "Yes, please",
  "onboard.push.later": "Maybe later",
  "onboard.push.on": "Reminders are on.",
  "onboard.push.denied":
    "The browser is blocking notifications. You can turn them on in Settings later.",
  "onboard.push.unsupported": "This browser cannot send reminders.",
  "onboard.push.error": "That did not work just now. You can turn them on in Settings later.",
  "onboard.push.ios.title": "Add Phoenix to your home screen first",
  "onboard.push.ios.body":
    "On iPhone, Phoenix needs to live on the home screen to remind you. " +
    "Tap the Share button (the square with the arrow) at the bottom of Safari and choose " +
    "“Add to Home Screen”. Then open Phoenix from there.",
  "onboard.push.ios.added": "I have added it",

  "onboard.done.title": "All set.",
  "onboard.done.nothing": "We took it easy this time. Everything can be filled in later.",
  "onboard.done.day": "You are on day",
  "onboard.done.sinceInfusion": "since the treatment.",
  "onboard.done.photos.one": "1 picture is saved for review.",
  "onboard.done.photos.many": "pictures are saved for review.",
  "onboard.done.body": "Height and weight are saved.",
  "onboard.done.open": "Open Today",

  // ===========================================================================
  // APPENDED BLOCK — Idag + Min resa (data views). Keys: idag2.* and resa.*
  // Mirrors the Swedish block at the end of `sv`; keep the two in step.
  // ===========================================================================
  "idag2.greeting.morning": "Good morning",
  "idag2.greeting.day": "Good day",
  "idag2.greeting.evening": "Good evening",
  "idag2.greeting.night": "Good night",
  "idag2.needLogin": "Sign in first and I will fetch your day.",
  "idag2.cycle.day": "Day",
  "idag2.cycle.of": "of",
  "tabs.mat": "Food",
  "mat.title": "Food",
  "mat.hint": "Today's menu, the week ahead and the shopping list. An offer, never a quota — half a portion eaten beats a full one left standing.",
  "mat.loading": "Fetching the menu …",
  "mat.failed": "The menu could not be fetched right now.",
  "mat.retry": "Try again",
  "mat.slot.frukost": "Breakfast",
  "mat.slot.mellanmal": "Snack",
  "mat.slot.lunch": "Lunch",
  "mat.slot.middag": "Dinner",
  "mat.today.title": "Today's menu",
  "mat.today.soft": "Sore-mouth mode: everything on today's menu is soft, cool and citrus-free.",
  "mat.today.totals": "The whole menu: about %KCAL% kcal and %PROT% g protein.",
  "mat.today.target": "Your protein target is %LO%–%HI% g per day.",
  "mat.today.noquota": "The menu is counted generously — not everything needs eating.",
  "mat.detail.ingredients": "Ingredients",
  "mat.detail.steps": "How to",
  "mat.week.title": "The week ahead",
  "mat.week.soft": "soft day",
  "mat.shop.title": "Shopping list",
  "mat.shop.hint": "Everything for the next seven days of menus. Tick items off in the store — the list remembers until the week ends.",
  "mat.shop.reset": "Reset the list",
  "idag2.band.title": "The next two weeks",
  "idag2.band.hint": "Tap a day to see what usually happens then.",
  "idag2.band.note": "Expected pattern for your regimen — your days may differ.",
  "idag2.band.adjusted": "The forecast is tuned to your own check-ins — every answer sharpens it.",
  "idag2.band.tunedWin": "Your days point to day %FROM%–%TO% (expected %BFROM%–%BTO%).",
  "idag2.band.infection": "Sensitive to infection",
  "idag2.band.calm": "Nothing in particular is expected on this day.",
  "idag2.band.noNext": "The next treatment has not been entered yet.",
  "idag2.anchor.title": "No treatment day entered yet.",
  "idag2.anchor.body": "Enter the day of your latest treatment and I will know where in the cycle you are.",
  "idag2.anchor.label": "Date of the latest treatment",
  "idag2.anchor.save": "Save the day",
  "idag2.anchor.saved": "Thank you. Now I know where in the cycle you are.",
  "idag2.anchor.error": "It could not be saved right now. Try again in a moment.",
  "idag2.anchor.eventTitle": "Treatment",
  "idag2.weight.title": "Would you like to weigh yourself?",
  "idag2.weight.hint": "Write your weight in kilos, or skip it.",
  "idag2.weight.unit": "kg",
  "idag2.sleep.title": "How many hours did you sleep?",
  "idag2.sleep.hint": "Roughly is plenty, or skip it.",
  "idag2.sleep.unit": "hours",
  "idag2.appetite.title": "How is your appetite?",
  "idag2.appetite.none": "None",
  "idag2.appetite.little": "Small",
  "idag2.appetite.ok": "Quite all right",
  "idag2.appetite.good": "Good",
  "idag2.checkin.save": "Save",
  "idag2.checkin.skip": "Skip",
  "idag2.checkin.saved": "Thank you — it is saved.",
  "idag2.num.invalid": "Write a number, or skip it.",
  "idag2.plan.title": "Today's plan",
  "idag2.plan.empty": "Nothing planned today. Rest is a task too.",
  "idag2.plan.markDone": "Done",
  "idag2.plan.isDone": "Done",
  "idag2.streak.one": "1 day in a row",
  "idag2.streak.many": "days in a row",
  "idag2.detail.why": "Why",
  "idag2.detail.how": "How to do it",
  "idag2.detail.john": "Do it together with John in Talk",
  "idag2.brief.here": "Your weekly letter is here",
  "idag2.brief.open": "Read the weekly letter",

  "resa.tab": "My journey",
  "resa.title": "My journey",
  "resa.loading": "Loading your journey …",
  "resa.error": "Could not load it right now. Try again in a moment.",
  "resa.retry": "Try again",
  "resa.needLogin": "Sign in first and I will show your journey.",
  "resa.weight.title": "Weight",
  "resa.weight.period": "The last two months.",
  "resa.weight.latest": "Latest weigh-in:",
  "resa.weight.unit": "kg",
  "resa.weight.empty": "Too few measurements yet — weigh yourself a few mornings and the line will appear.",
  "resa.gki.title": "Your readings (GKI)",
  "resa.gki.hint": "From the finger sticks: fasting in the morning and before dinner.",
  "resa.gki.latest": "Latest value:",
  "resa.gki.zone.deep": "Deep therapeutic ketosis",
  "resa.gki.zone.high": "High therapeutic ketosis",
  "resa.gki.zone.moderate": "Moderate ketosis",
  "resa.gki.zone.low": "Low ketosis",
  "resa.gki.zone.none": "No ketosis",
  "resa.mood.title": "Mood",
  "resa.mood.hint": "The last 30 days. The higher the dot, the better the day.",
  "resa.mood.latest": "Latest:",
  "resa.mood.empty": "No answers yet this month.",
  "resa.brief.title": "The weekly letter",
  "resa.brief.weekOf": "The week that began",
  "resa.brief.more": "Read all of it",
  "resa.brief.less": "Show less",

  // ===== prata (appended section — keep at the end of the en table) =====
  "prata2.starter": "Hello Björn. Ask me anything — or take a picture of your food.",
  "prata2.photo.group": "Take a picture",
  "prata2.btn.food": "Can I eat this?",
  "prata2.btn.meds": "Photograph the medicine",
  "prata2.btn.reading": "Photograph the meter",
  "prata2.ask.food": "Can I eat this?",
  "prata2.ask.meds": "Here is my medicine.",
  "prata2.ask.reading": "Here is my reading.",
  "prata2.voice.wait": "One moment …",
  "prata2.starter.john": "I am John. We take it slow — write when you want to start, and we breathe together.",
};

const tables: Record<Lang, Partial<Record<StringKey, string>>> = { sv, en };

let current: Lang = (localStorage.getItem("phoenix.lang") as Lang | null) ?? "sv";

export function setLang(lang: Lang): void {
  current = lang;
  localStorage.setItem("phoenix.lang", lang);
}

export function getLang(): Lang {
  return current;
}

export function t(key: StringKey): string {
  return tables[current][key] ?? sv[key] ?? en[key] ?? key;
}
